import { t } from "i18next";
import { z } from "zod";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  commandUnitSchema,
  FormSectionSchema,
  IncidentFormStatusSchema,
  IncidentFormTypeSchema,
  LocationSchema,
  nonFutureDateTimeSchema,
  OrganisationRoleSchema,
  responsibleCustodyForceSchema,
  shortTextSchema,
  yesNoSchema,
  type CreateIncidentMinimumFields,
  type IncidentWorkItemWithContext,
  type StaticOptionsList,
} from "../../types/index.js";
import { tk } from "../../types/Translation.js";
import { responsibleCustodyForceOptions } from "../common/custodySuite.js";

/**
 * One list for both the detention and the incident location, so their options
 * cannot drift apart.
 */
const railwayLocationValues = [
  "policeStationsCustody",
  "onTrain",
  "concourse",
  "platform",
  "stationToilet",
  "stationForecourt",
  "carPark",
  "onTracksAtStation",
  "onTracksAwayFromStation",
  "lineside",
  "bridge",
  "levelCrossing",
  "disusedRailwayOrStation",
  "tunnel",
  "depot",
  "otherRailwayInfrastructure",
  "offBtpJurisdiction",
  "btpOffice",
  "ticketBarriers",
  "hotelWithinRailwayEnvironment",
  "publicHouse",
  "other",
] as const;

const railwayLocationSchema = z.enum(railwayLocationValues);

const railwayLocationOptions: StaticOptionsList = railwayLocationValues.map(
  (value) => ({
    value,
    label: tk(`detentionDetails.locationType.${value}`),
  }),
);

const custodyLocationType =
  "policeStationsCustody" satisfies (typeof railwayLocationValues)[number];

/**
 * As much of a detention section as this file reads. Sections are stored
 * untyped and a record may predate any given question, so anything beyond the
 * fields declared here is deliberately left unconstrained.
 */
const recordedCustodySchema = z.object({
  custodyNumber: z.object({ value: z.string().nullish() }).nullish(),
  locationType: z.object({ value: z.string().nullish() }).nullish(),
});

/**
 * Whether the record carries a custody number that its own location type
 * cannot account for - one recorded while the question was asked of every
 * detention rather than of custody alone.
 *
 * A number recorded alongside custody belongs to that answer, so it is not
 * reported here: an officer correcting the location is meant to leave the
 * number behind with it.
 */
function hasCustodyNumberRecordedAwayFromCustody(
  incidentWorkItem: IncidentWorkItemWithContext | undefined,
): boolean {
  const detentionDetails = incidentWorkItem?.forms
    .find(
      (form) =>
        form.formType === IncidentFormTypeSchema.enum.section136 &&
        form.status === IncidentFormStatusSchema.enum.active,
    )
    ?.sections.find(
      (section) => section.id === FormSectionSchema.enum.detentionDetails,
    )?.data;

  const recorded = recordedCustodySchema.safeParse(detentionDetails);

  // An empty string is a question that was shown and left blank, not an
  // answer; treating it as one would make the field required and reject the
  // record it came from.
  return (
    (recorded.data?.custodyNumber?.value ?? "") !== "" &&
    recorded.data?.locationType?.value !== custodyLocationType
  );
}

/**
 * Detentions recorded before the question was asked of custody alone carry a
 * custody number whatever their location type. A field the form stops showing
 * is blanked and written back empty, so the question stays visible wherever
 * such an answer exists and nothing else on the record explains it.
 */
const asksForCustodyNumber = ({
  formValues,
  incidentWorkItem,
}: {
  formValues: { locationType?: { value: string } | null };
  incidentWorkItem?: IncidentWorkItemWithContext;
}) =>
  formValues.locationType?.value === custodyLocationType ||
  hasCustodyNumberRecordedAwayFromCustody(incidentWorkItem);

const heldInCustody = ({
  formValues,
}: {
  formValues: { locationType?: { value: string } | null };
}) => formValues.locationType?.value === custodyLocationType;

const incidentWasElsewhere = ({
  formValues,
}: {
  formValues: { incidentAndLocationSameLocation?: { value: string } | null };
}) => formValues.incidentAndLocationSameLocation?.value === "no";

/**
 * Unbuilt, so a workflow can compose rules of its own onto these questions. A
 * new builder on every call: the builder is mutable, so a shared instance would
 * carry one workflow's rules into every other.
 */
export const detentionDetailsFormBuilder = () =>
  new MultiPageFormBuilder()
    .withFieldAlwaysVisible({
      fieldName: "dateTimeOfDetention", // 136-detention-2.0
      type: "date-time",
      timeLabel: tk("detentionDetails.timeOfDetention"),
      dateLabel: tk("detentionDetails.dateOfDetention"),
      disableFuture: true,
      ...new FieldBuilder().withSchema(nonFutureDateTimeSchema).build(),
    })
    .withFieldAlwaysVisible({
      fieldName: "orgResponsibleForDetention", // 136-detention-3.0
      type: "organisation",
      ...new FieldBuilder()
        .withSchema(commandUnitSchema)
        .captureValueLabel()
        .build(),
      filterByRoles: [OrganisationRoleSchema.enum.PoliceCommandUnit],
      label: tk("detentionDetails.responsibleForDetention"),
      defaultValue: ({ incidentWorkItem }) => {
        // Get create incident information
        const createIncidentForm = incidentWorkItem?.forms.find(
          (form) =>
            form.formType === IncidentFormTypeSchema.enum.createIncident,
        );

        const createIncidentSectionData = createIncidentForm?.sections.find(
          (section) => section.id === FormSectionSchema.enum.createIncidentMain,
        )?.data as Partial<CreateIncidentMinimumFields> | undefined;

        return createIncidentSectionData?.commandUnit ?? null;
      },
    })
    .withFieldAlwaysVisible({
      fieldName: "locationDetentionTookPlace", // 136-detention-3.0.1
      type: "location",
      ...new FieldBuilder().withSchema(LocationSchema).build(),
      label: tk("detentionDetails.locationDetentionTookPlace"),
    })
    .withFieldAlwaysVisible({
      fieldName: "locationType", // 136-detention-3.0.1.1
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(railwayLocationSchema)
        .captureValueLabel()
        .build(),
      label: tk("detentionDetails.locationType.title"),
      options: railwayLocationOptions,
    })
    .withFieldConditionallyVisible({
      fieldName: "locationTypeOther", // 136-detention-3.0.1.1.1.1
      type: "textbox",
      ...new FieldBuilder().withSchema(shortTextSchema).build(),
      isVisible: ({ formValues }) => formValues.locationType?.value === "other",
      label: tk("triageIncident.provideDetails"),
    })
    .withFieldConditionallyVisible({
      fieldName: "custodySuiteName", // 136-detention-3.0.1.1.2
      type: "textbox",
      ...new FieldBuilder().withSchema(shortTextSchema).build(),
      label: tk("custodySuite.name"),
      requiredLabel: tk("custodySuite.nameRequired"),
      isVisible: heldInCustody,
    })
    .withFieldConditionallyVisible({
      fieldName: "policeForceResponsibleForCustodySuite", // 136-detention-3.0.1.1.3
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(responsibleCustodyForceSchema)
        .captureValueLabel()
        .build(),
      label: tk("custodySuite.responsibleForce.title"),
      requiredLabel: tk("custodySuite.responsibleForce.required"),
      options: responsibleCustodyForceOptions,
      isVisible: heldInCustody,
    })
    /**
     * Nullable because the question is also shown where a record carries a
     * custody number its stored location cannot account for: requiring one of
     * every such record would leave a mis-selected location impossible to
     * correct. A detention in custody must still carry a number, enforced by the
     * `withFormValidation` rule on this form.
     */
    .withFieldConditionallyVisible({
      fieldName: "custodyNumber", // 136-detention-3.0.1.1.4
      type: "textbox",
      ...new FieldBuilder().withNullableSchema(shortTextSchema).build(),
      label: tk("detentionDetails.custodyNumber"),
      isVisible: asksForCustodyNumber,
    })
    .withReadonlyFieldConditionallyVisible({
      fieldName: "custodyNumberDescriptionBanner", // 136-detention-3.0.1.1.5
      type: "banner",
      bannerType: "info",
      body: tk("detentionDetails.custodyNumberDescriptionBanner"),
      isVisible: asksForCustodyNumber,
    })
    /**
     * Nullable so that a record written before this question existed still parses
     * against the current definition. A record that does not parse is treated as
     * incomplete, and a question it was never shown is one it cannot answer.
     */
    .withFieldAlwaysVisible({
      fieldName: "incidentAndLocationSameLocation", // 136-detention-6.1
      type: "radio",
      ...new FieldBuilder()
        .withNullableSchema(yesNoSchema)
        .captureValueLabel()
        .build(),
      label: tk("detentionDetails.incidentAndLocationSameLocation"),
      options: [
        { label: tk("common.yes"), value: "yes" },
        { label: tk("common.no"), value: "no" },
      ],
    })
    .withFieldConditionallyVisible({
      fieldName: "incidentLocation", // 136-detention-6.2
      type: "location",
      ...new FieldBuilder().withSchema(LocationSchema).build(),
      label: tk("detentionDetails.incidentLocation"),
      requiredLabel: tk("detentionDetails.incidentLocationRequired"),
      isVisible: incidentWasElsewhere,
    })
    .withFieldConditionallyVisible({
      fieldName: "commandUnit", // 136-detention-7.1
      type: "organisation",
      filterByRoles: [OrganisationRoleSchema.enum.PoliceCommandUnit],
      ...new FieldBuilder()
        .withSchema(commandUnitSchema)
        .captureValueLabel()
        .build(),
      label: tk("detentionDetails.commandUnit"),
      requiredLabel: tk("detentionDetails.commandUnitRequired"),
      isVisible: incidentWasElsewhere,
    })
    .withFieldConditionallyVisible({
      fieldName: "locationTypeIncident", // 136-detention-7.3
      type: "dropdown",
      ...new FieldBuilder()
        .withSchema(railwayLocationSchema)
        .captureValueLabel()
        .build(),
      label: tk("detentionDetails.locationType.title"),
      requiredLabel: tk("detentionDetails.locationType.required"),
      options: railwayLocationOptions,
      isVisible: incidentWasElsewhere,
    })
    .withFormValidation(({ formValues, t }) =>
      formValues.locationType?.value === custodyLocationType &&
      formValues.custodyNumber === null
        ? [
            {
              fieldName: "custodyNumber",
              message: t("detentionDetails.custodyNumberRequired"),
            },
          ]
        : [],
    )
    .withPage([
      "dateTimeOfDetention", // 136-detention-2.0
    ])
    .withPage([
      "orgResponsibleForDetention", // 136-detention-3.0
    ])
    .withPage([
      "locationDetentionTookPlace", // 136-detention-3.0.1
    ])
    .withPage([
      "locationType", // 136-detention-3.0.1.1
      "locationTypeOther", // 136-detention-3.0.1.1.1.1
    ])
    // The custody questions sit on their own page, in the order the MCA form
    // asks them, so a person held in custody answers the same questions the same
    // way whichever form records it.
    .withPage([
      "custodyNumber", // 136-detention-3.0.1.1.4
      "custodyNumberDescriptionBanner", // 136-detention-3.0.1.1.5
      "custodySuiteName", // 136-detention-3.0.1.1.2
      "policeForceResponsibleForCustodySuite", // 136-detention-3.0.1.1.3
    ])
    .withPage([
      "incidentAndLocationSameLocation", // 136-detention-6.1
      "incidentLocation", // 136-detention-6.2
    ])
    .withPage([
      "commandUnit", // 136-detention-7.1
      "locationTypeIncident", // 136-detention-7.3
    ]);

export const formSpecification = detentionDetailsFormBuilder().build({ t });

export type DetentionDetailsFormData = ReturnType<
  typeof formSpecification
>["formType"];
